import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Users, Shield, TrendingUp, MapPin, Clock, Sparkles, Plane, Heart, ChevronDown } from 'lucide-react';
import { getJobs } from '../utils/storage';

interface CountryData {
  name: string;
  cities: string[];
  jobCount: number;
}

const countryMeta: Record<string, { image: string; flag: string; hoverColor: string }> = {
  'Países Bajos': {
    image: 'https://images.unsplash.com/photo-1512470876302-972faa2aa9a4?w=800&q=80',
    flag: '🇳🇱',
    hoverColor: 'group-hover:from-accent/70'
  },
  'Irlanda': {
    image: 'https://images.unsplash.com/photo-1590089415225-401ed6f9db8e?w=800&q=80',
    flag: '🇮🇪',
    hoverColor: 'group-hover:from-primary/70'
  },
  'Alemania': {
    image: 'https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800&q=80',
    flag: '🇩🇪',
    hoverColor: 'group-hover:from-yellow-600/70'
  },
  'Francia': {
    image: 'https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=800&q=80',
    flag: '🇫🇷',
    hoverColor: 'group-hover:from-blue-600/70'
  },
  'Reino Unido': {
    image: 'https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=800&q=80',
    flag: '🇬🇧',
    hoverColor: 'group-hover:from-red-600/70'
  },
  'Portugal': {
    image: 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=800&q=80',
    flag: '🇵🇹',
    hoverColor: 'group-hover:from-green-600/70'
  },
  'Italia': {
    image: 'https://images.unsplash.com/photo-1515542622106-78bda8ba0e5b?w=800&q=80',
    flag: '🇮🇹',
    hoverColor: 'group-hover:from-green-700/70'
  },
  'Cruceros Europa': {
    image: 'https://images.unsplash.com/photo-1548574505-5e239809ee19?w=800&q=80',
    flag: '🚢',
    hoverColor: 'group-hover:from-blue-500/70'
  },
  'Cruceros Fluviales': {
    image: 'https://images.unsplash.com/photo-1548574505-5e239809ee19?w=800&q=80',
    flag: '🚢',
    hoverColor: 'group-hover:from-blue-500/70'
  },
};

const defaultCountryMeta = {
  image: 'https://images.unsplash.com/photo-1488085061387-422e29b40080?w=800&q=80',
  flag: '🌍',
  hoverColor: 'group-hover:from-gray-600/70'
};

const Home: React.FC = () => {
  const heroImages = [
    'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=1920&q=80', // Restaurant/hospitality
    'https://images.unsplash.com/photo-1555396273-367ea4eb4db5?w=1920&q=80', // Restaurant service
    'https://images.unsplash.com/photo-1559329007-40df8a9345d8?w=1920&q=80', // Amsterdam canal houses
    'https://images.unsplash.com/photo-1549918864-48ac978761a4?w=1920&q=80', // Dublin Ireland
  ];

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [destinations, setDestinations] = useState<CountryData[]>([]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 30000); // Change image every 30 seconds

    return () => clearInterval(interval);
  }, [heroImages.length]);

  useEffect(() => {
    const loadDestinations = async () => {
      const jobs = await getJobs();
      const activeJobs = jobs.filter(job => job.isActive);

      const countryMap = new Map<string, Set<string>>();
      const countryJobCount = new Map<string, number>();

      activeJobs.forEach(job => {
        if (job.country) {
          if (!countryMap.has(job.country)) {
            countryMap.set(job.country, new Set());
            countryJobCount.set(job.country, 0);
          }
          countryMap.get(job.country)!.add(job.location);
          countryJobCount.set(job.country, (countryJobCount.get(job.country) || 0) + 1);
        }
      });

      const destinationsData: CountryData[] = Array.from(countryMap.entries()).map(([country, cities]) => ({
        name: country,
        cities: Array.from(cities).slice(0, 3),
        jobCount: countryJobCount.get(country) || 0
      }));

      destinationsData.sort((a, b) => b.jobCount - a.jobCount);
      setDestinations(destinationsData);
    };

    loadDestinations();
  }, []);

  return (
    <div>
      <section className="relative h-screen min-h-[600px] flex items-center overflow-hidden">
        {heroImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
              index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url('${image}')`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-primary-dark/90 via-primary/85 to-accent/90"></div>
          </div>
        ))}
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <div className="flex justify-center mb-6">
              <Sparkles className="w-16 h-16 text-yellow-300 animate-pulse" />
            </div>
            <h1 className="text-6xl md:text-7xl font-extrabold mb-6 leading-tight drop-shadow-2xl">
              ¡Tu Aventura Laboral Empieza Aquí! 🌍
            </h1>
            <p className="text-2xl md:text-3xl mb-8 font-light drop-shadow-lg">
              Descubre trabajos increíbles alrededor del mundo y vive la experiencia de tu vida
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/jobs"
                className="inline-flex items-center gap-2 bg-white text-primary px-8 py-4 rounded-full font-bold text-lg hover:scale-105 hover:shadow-2xl transition-all duration-300"
              >
                <Plane className="w-5 h-5" />
                ¡Ver Ofertas Ahora!
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 border-3 border-white bg-white/20 backdrop-blur-sm px-8 py-4 rounded-full font-bold text-lg hover:bg-white hover:text-primary transition-all duration-300"
              >
                <Heart className="w-5 h-5" />
                Contáctanos
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 md:bottom-12 lg:bottom-16 xl:bottom-24 left-0 right-0 z-10 hidden lg:flex justify-center animate-bounce">
          <div className="flex flex-col items-center text-white">
            <span className="text-xs md:text-sm font-medium mb-1 md:mb-2 drop-shadow-lg">Descubre más</span>
            <ChevronDown className="w-6 h-6 md:w-8 md:h-8 drop-shadow-lg" />
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <h2 className="text-4xl font-bold mb-6 text-gray-800">
              Posiciones Disponibles 💼
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Trabajamos con restaurantes y hoteles verificados en toda Europa.
              Ya seas profesional con experiencia o estés comenzando tu carrera en hostelería,
              tenemos oportunidades perfectas para ti.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <Link to="/jobs?search=Camarero" className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all text-center cursor-pointer border-t-4 border-accent">
              <div className="text-6xl mb-4">🍽️</div>
              <h3 className="font-bold text-2xl mb-3 text-primary">Camarero/a</h3>
              <p className="text-gray-600 text-lg">Servicio en restaurantes, hoteles y cafeterías</p>
            </Link>
            <Link to="/jobs?search=Chef" className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all text-center cursor-pointer border-t-4 border-primary">
              <div className="text-6xl mb-4">👨‍🍳</div>
              <h3 className="font-bold text-2xl mb-3 text-primary">Chef / Cocinero/a</h3>
              <p className="text-gray-600 text-lg">Cocinas profesionales en establecimientos de calidad</p>
            </Link>
            <Link to="/jobs?search=Limpieza" className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all text-center cursor-pointer border-t-4 border-green-500">
              <div className="text-6xl mb-4">🧹</div>
              <h3 className="font-bold text-2xl mb-3 text-primary">Limpieza</h3>
              <p className="text-gray-600 text-lg">Servicios de limpieza en hoteles y establecimientos</p>
            </Link>
          </div>
        </div>
      </section>

      {destinations.length > 0 && (
        <section className="py-12 bg-gradient-to-b from-white to-pink-50">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl font-bold text-center mb-4 text-gray-800">
              Nuestros Destinos 🗺️
            </h2>
            <p className="text-center text-xl text-gray-600 mb-12">
              Oportunidades en hostelería en {destinations.map(d => d.name).join(', ').replace(/, ([^,]*)$/, ' e $1')}
            </p>
            <div className={`grid gap-8 max-w-5xl mx-auto ${destinations.length === 1 ? 'md:grid-cols-1 max-w-md' : destinations.length === 2 ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
              {destinations.map((destination) => {
                const meta = countryMeta[destination.name] || defaultCountryMeta;
                const citiesText = destination.cities.length > 0
                  ? destination.cities.join(', ') + (destination.cities.length >= 3 ? ' y más' : '')
                  : 'Descubre oportunidades';

                return (
                  <Link
                    key={destination.name}
                    to={`/jobs?country=${encodeURIComponent(destination.name)}`}
                    className="relative h-80 rounded-2xl overflow-hidden group cursor-pointer shadow-lg hover:shadow-2xl transition-all duration-300"
                  >
                    <div
                      className="absolute inset-0 bg-cover bg-center"
                      style={{
                        backgroundImage: `url('${meta.image}')`,
                      }}
                    >
                      <div className={`absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent ${meta.hoverColor} transition-all duration-300`}></div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                      <h3 className="text-3xl font-bold mb-2">{destination.name} {meta.flag}</h3>
                      <p className="text-lg">{citiesText}</p>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="py-12 bg-gradient-to-b from-pink-50 to-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center mb-12 text-gray-800">
              Cómo Funciona 🚀
            </h2>
            <div className="space-y-8">
              <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                <div className="bg-pink-500 text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">1</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Busca y Aplica</h3>
                  <p className="text-gray-600">Explora nuestras ofertas verificadas y aplica a las que más te interesen. Todas incluyen contrato desde España.</p>
                </div>
              </div>
              <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                <div className="bg-orange-500 text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">2</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Recibe Asesoría Personalizada</h3>
                  <p className="text-gray-600">Nuestro equipo te guía con la documentación, permisos, y preparación para el viaje y la entrevista.</p>
                </div>
              </div>
              <div className="flex items-start gap-6 bg-white p-6 rounded-2xl shadow-lg">
                <div className="bg-purple-500 text-white w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 text-xl font-bold">3</div>
                <div>
                  <h3 className="text-xl font-bold mb-2">Comienza Tu Aventura</h3>
                  <p className="text-gray-600">¡Empieza a trabajar en el extranjero! Te seguimos apoyando durante tu adaptación y primeras semanas.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-gray-800">
              Tu Puerta de Entrada al Trabajo en Hostelería 🌍
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-8">
              En <span className="font-bold text-primary">TrabajoFuera</span>, conectamos a profesionales españoles
              con oportunidades en hostelería verificadas en toda Europa. No solo te ayudamos a encontrar un empleo,
              te acompañamos en cada paso de tu aventura internacional.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              Desde la búsqueda inicial hasta tu primer día de trabajo, nuestro equipo te ofrece apoyo personalizado,
              asesoría en documentación, y seguimiento continuo para que tu experiencia sea exitosa y sin estrés.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-b from-white to-orange-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-800">
            ¿Por Qué Elegir TrabajoFuera? ✨
          </h2>
          <p className="text-center text-xl text-gray-600 mb-12">Porque tu futuro merece lo mejor</p>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
              <div className="bg-gradient-to-br from-blue-400 to-blue-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-800">Destinos en Europa 🌎</h3>
              <p className="text-gray-600 text-lg">
                Múltiples destinos con oportunidades en hostelería de calidad
              </p>
            </div>
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
              <div className="bg-gradient-to-br from-green-400 to-green-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-800">100% Seguro 🛡️</h3>
              <p className="text-gray-600 text-lg">
                Todas las empresas verificadas. Tu tranquilidad es nuestra prioridad
              </p>
            </div>
            <div className="text-center bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300">
              <div className="bg-gradient-to-br from-purple-400 to-purple-600 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-10 h-10 text-white" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-800">Apoyo Total 🤝</h3>
              <p className="text-gray-600 text-lg">
                Te acompañamos en cada paso. ¡Nunca estarás solo en este viaje!
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-orange-50 via-pink-50 to-yellow-50">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center mb-4 text-gray-800">
            Nuestros Servicios 🚀
          </h2>
          <p className="text-center text-xl text-gray-600 mb-12">Todo lo que necesitas para tu éxito</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-orange-400">
              <div className="bg-orange-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <TrendingUp className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-800">Búsqueda de Empleo 💼</h3>
              <p className="text-gray-600 text-lg">
                Las mejores ofertas seleccionadas especialmente para ti
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-pink-400">
              <div className="bg-pink-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-8 h-8 text-pink-600" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-800">Asesoría de Relocalización 📍</h3>
              <p className="text-gray-600 text-lg">
                Te ayudamos con todos los trámites. ¡Sin estrés!
              </p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 border-t-4 border-yellow-400">
              <div className="bg-yellow-100 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-2xl font-bold mb-3 text-gray-800">Preparación de Entrevistas ⭐</h3>
              <p className="text-gray-600 text-lg">
                Te preparamos para brillar en cada entrevista
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="relative py-32 overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-fixed"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1920&q=80')",
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/95 via-primary/95 to-accent/95"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10 text-white">
          <h2 className="text-5xl md:text-6xl font-extrabold mb-6 drop-shadow-2xl">
            ¿Listo para la Aventura? 🎉
          </h2>
          <p className="text-2xl md:text-3xl mb-4 font-light drop-shadow-lg">
            Miles de españoles ya han encontrado su trabajo soñado en el extranjero.
          </p>
          <p className="text-xl md:text-2xl mb-8 font-light drop-shadow-lg">
            Con ofertas verificadas, contratos desde España, y apoyo continuo. ¡Ahora es tu turno!
          </p>
          <Link
            to="/jobs"
            className="inline-flex items-center gap-2 bg-accent hover:bg-accent-dark text-white px-10 py-5 rounded-full font-bold text-xl hover:scale-110 hover:shadow-2xl transition-all duration-300"
          >
            <Sparkles className="w-6 h-6" />
            ¡Explorar Ofertas Ya!
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;